// Sign-up attribution. Captures UTM params + referrer + landing path the moment a
// visitor first lands on the site, stashes them in localStorage so they survive the
// magic-link redirect, then writes them to Supabase the first time the user signs in.
//
// One row per user. Subsequent sign-ins do not overwrite the original attribution,
// so the "first touch" channel sticks (which is the useful one for ad attribution).

import { supabase } from './supabase';

const STORAGE_KEY = 'mdt_attribution';
const CAPTURED_FLAG = 'mdt_attribution_captured';

interface AttributionContext {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_path: string;
  user_agent: string;
  captured_at: number;
}

/**
 * Capture the visitor's attribution context. Idempotent within a browser session:
 * once we've captured a non-empty context we do not overwrite it, so the first-touch
 * channel is preserved across page navigations.
 *
 * Call this once on app mount (in main.tsx) BEFORE any navigation.
 */
export function captureLandingContext() {
  try {
    if (localStorage.getItem(CAPTURED_FLAG) === '1') return;

    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source');
    const utm_medium = params.get('utm_medium');
    const utm_campaign = params.get('utm_campaign');
    const utm_term = params.get('utm_term');
    const utm_content = params.get('utm_content');
    const referrer = document.referrer || null;

    // Only capture if there's anything interesting to capture. A user who arrives at
    // the homepage with no UTMs and no referrer is treated as "direct" and we still
    // record that, but we set the flag so we don't keep re-checking.
    const ctx: AttributionContext = {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      referrer,
      landing_path: window.location.pathname,
      user_agent: navigator.userAgent.slice(0, 200),
      captured_at: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    localStorage.setItem(CAPTURED_FLAG, '1');
  } catch {
    // Storage unavailable. Skip.
  }
}

/**
 * Write the captured attribution to Supabase for this user. Called once at first
 * sign-in. Subsequent calls are no-ops (the table uses ON CONFLICT DO NOTHING).
 *
 * After writing we clear the local stash so a future sign-in from a fresh visit
 * captures fresh attribution for a different account.
 */
export async function recordAttributionIfNeeded(userId: string): Promise<void> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    const ctx = JSON.parse(stored) as AttributionContext;

    // ON CONFLICT (user_id) DO NOTHING - first-touch sticks.
    await supabase
      .from('user_attribution')
      .upsert(
        {
          user_id: userId,
          utm_source: ctx.utm_source,
          utm_medium: ctx.utm_medium,
          utm_campaign: ctx.utm_campaign,
          utm_term: ctx.utm_term,
          utm_content: ctx.utm_content,
          referrer: ctx.referrer,
          landing_path: ctx.landing_path,
          user_agent: ctx.user_agent,
        },
        { onConflict: 'user_id', ignoreDuplicates: true }
      );

    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CAPTURED_FLAG);
  } catch {
    // Network or storage failure. Acceptable - attribution is best-effort.
  }
}
